import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useEffect, useRef, useState } from 'react';
import { Save, Plus, X, Server, Database, Mail, Brain, Shield, Globe, CheckCircle, XCircle, Loader2, Cloud, Image as ImageIcon, User, Bell } from 'lucide-react';
import { Select, SelectOption } from '@/components/ui/Select';
import InputField from '@/components/common/form-fields/InputField';
import PasswordInputField from '@/components/ui/PasswordInputField';
import CopyableInputField from '@/components/ui/CopyableInputField';
import { Toggle } from '@/components/ui/Toggle';
import { CreateTenantDto, UpdateTenantDto, TenantStatus } from '@/types/tenant.types';
import { useDebounce } from '@/utils/debounce';
import tenantService from '@/services/tenantService';

// Validation schemas matching SDK DTOs
const domainConfigSchema = yup.object({
  admin_url: yup.string().url('Must be a valid URL').required('UI URL is required'),
  api_url: yup.string().url('Must be a valid URL').required('SERVICE URL is required'),
});

const s3ConfigSchema = yup.object({
  accessKeyId: yup.string().required('Access Key ID is required'),
  secretAccessKey: yup.string().required('Secret Access Key is required'),
  endpoint: yup.string().required('Endpoint is required'),
  bucket_name: yup.string().optional(),
  region: yup.string().optional(),
});

const emailConfigSchema = yup.object({
  host_name: yup.string().required('Host name is required'),
  host_port: yup.number().transform((value, originalValue) => originalValue === '' ? undefined : value).positive('Must be a positive number').required('Host port is required'),
  client_id: yup.string().required('Client ID is required'),
  client_secret: yup.string().required('Client secret is required'),
  provider: yup.string().required('Provider is required'),
  from: yup.string().required('From address is required'),
});

const googleCloudCredentialsSchema = yup.object({
  type: yup.string().required('Type is required'),
  project_id: yup.string().required('Project ID is required'),
  private_key_id: yup.string().required('Private key ID is required'),
  private_key: yup.string().required('Private key is required'),
  client_email: yup.string().email('Must be valid email').required('Client email is required'),
  client_id: yup.string().required('Client ID is required'),
  auth_uri: yup.string().url('Must be valid URL').required('Auth URI is required'),
  token_uri: yup.string().url('Must be valid URL').required('Token URI is required'),
  auth_provider_x509_cert_url: yup.string().url('Must be valid URL').required('Auth provider cert URL is required'),
  client_x509_cert_url: yup.string().url('Must be valid URL').required('Client cert URL is required'),
  universe_domain: yup.string().required('Universe domain is required'),
});

const llmConfigSchema = yup.object({
  api_key: yup.string().required('API Key is required'),
  model_name: yup.string().required('Model name is required'),
  temperature: yup.string()
    .required('Temperature is required')
    .test('is-valid-temperature', 'Temperature must be between 0 and 1', (value) => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0 && num <= 1;
    }),
});

const vectorStoreConfigSchema = yup.object({
  url: yup.string().url('Must be valid URL').required('URL is required'),
  token: yup.string().required('Token is required'),
});

const cloudflareAIGatewaySchema = yup.object({
  enabled: yup.boolean().default(false),
  token: yup.string().when('enabled', { is: true, then: (s) => s.required('Token is required when enabled'), otherwise: (s) => s.optional() }),
  account_id: yup.string().when('enabled', { is: true, then: (s) => s.required('Account ID is required when enabled'), otherwise: (s) => s.optional() }),
  gateway_id: yup.string().when('enabled', { is: true, then: (s) => s.required('Gateway ID is required when enabled'), otherwise: (s) => s.optional() }),
  cf_aig_byok_alias: yup.string().optional(),
}).optional();

const imageGenerationConfigSchema = yup.object({
  // Multi-select, so the form value is string[]. A string is still accepted because a
  // tenant saved before this was a dropdown carries a single (or comma-separated) value,
  // and onSubmit normalises both shapes before they reach the API.
  model: yup.mixed<string | string[]>().optional(),
  resolution: yup.string().optional(),
  aspect_ratio: yup.string().optional(),
  quality: yup.string().optional(),
  style: yup.string().optional(),
}).optional();

const avatarProvidersSchema = yup.object({
  d_id: yup.object({
    api_key: yup.string().optional(),
    base_url: yup.string().optional(),
  }).optional(),
  heygen: yup.object({
    api_key: yup.string().optional(),
    base_url: yup.string().optional(),
  }).optional(),
}).optional();

const webSearchSchema = yup.object({
  enabled: yup.boolean().default(false),
  provider: yup.string().optional(),
  tavily_api_key: yup.string().optional(),
}).optional();

const onesignalConfigSchema = yup.object({
  enabled: yup.boolean().default(false),
  app_id: yup.string().when('enabled', { is: true, then: (s) => s.required('App ID is required when enabled'), otherwise: (s) => s.optional() }),
  rest_api_key: yup.string().when('enabled', { is: true, then: (s) => s.required('REST API key is required when enabled'), otherwise: (s) => s.optional() }),
  safari_web_id: yup.string().optional(),
}).optional();

const aiConfigSchema = yup.object({
  llm: yup.object({
    provider: yup.string().required('LLM provider is required'),
    config: llmConfigSchema,
  }),
  vector_store: yup.object({
    provider: yup.string().required('Vector store provider is required'),
    config: vectorStoreConfigSchema,
  }),
  google_cloud_credentials: googleCloudCredentialsSchema,
  cloudflare_ai_gateway: cloudflareAIGatewaySchema,
  image_generation_config: imageGenerationConfigSchema,
  avatar_providers: avatarProvidersSchema,
  web_search: webSearchSchema,
});

const tenantLimitConfigSchema = yup.object({
  max_users: yup.number().transform((value, originalValue) => originalValue === '' ? null : value).min(1, 'Must be greater than 0').nullable().optional(),
  max_course_generations: yup.number().transform((value, originalValue) => originalValue === '' ? null : value).min(1, 'Must be greater than 0').nullable().optional(),
  max_document_stores: yup.number().transform((value, originalValue) => originalValue === '' ? null : value).min(1, 'Must be greater than 0').nullable().optional(),
  max_files_per_document_store: yup.number().transform((value, originalValue) => originalValue === '' ? null : value).min(1, 'Must be greater than 0').nullable().optional(),
}).optional();

const createTenantSchema = yup.object({
  name: yup.string().required('Tenant name is required'),
  tenant_key: yup
    .string()
    .matches(/^[a-z0-9_]+$/, 'Tenant key must contain only lowercase letters, numbers, and underscores')
    .required('Tenant key is required'),
  domains: yup.array().of(domainConfigSchema).min(1, 'At least one domain configuration is required'),
  s3_config: s3ConfigSchema,
  email_config: emailConfigSchema,
  ai_config: aiConfigSchema,
  status: yup.string().oneOf(Object.values(TenantStatus)),
  limits: tenantLimitConfigSchema.optional(),
  allowed_domains: yup.array().of(yup.string().url('Must be a valid URL')).optional(),
  onesignal_config: onesignalConfigSchema,
});

const updateTenantSchema = yup.object({
  name: yup.string(),
  domains: yup.array().of(domainConfigSchema),
  s3_config: s3ConfigSchema,
  email_config: emailConfigSchema,
  ai_config: aiConfigSchema,
  status: yup.string().oneOf(Object.values(TenantStatus)),
  limits: tenantLimitConfigSchema.optional(),
  allowed_domains: yup.array().of(yup.string().url('Must be a valid URL')).optional(),
  onesignal_config: onesignalConfigSchema,
});

interface TenantFormProps {
  initialData?: UpdateTenantDto | CreateTenantDto;
  onSubmit: (data: CreateTenantDto | UpdateTenantDto) => Promise<void>;
  isEdit?: boolean;
  isLoading?: boolean;
  tenantKey?: string;
}

// Status options
const statusOptions: SelectOption[] = [
  { label: 'Active', value: TenantStatus.ACTIVE },
  { label: 'Inactive', value: TenantStatus.INACTIVE },
  { label: 'Suspended', value: TenantStatus.SUSPENDED },
];

// AWS Region options
const awsRegionOptions: SelectOption[] = [
  { label: 'US East (N. Virginia)', value: 'us-east-1' },
  { label: 'US West (Oregon)', value: 'us-west-2' },
  { label: 'EU (Ireland)', value: 'eu-west-1' },
  { label: 'Asia Pacific (Mumbai)', value: 'ap-south-1' },
  { label: 'Asia Pacific (Singapore)', value: 'ap-southeast-1' },
];

// LLM Provider options
const llmProviderOptions: SelectOption[] = [
  { label: 'Google Generative AI', value: 'GOOGLE_GENERATIVE_AI' },
];

// Vector Store Provider options
const vectorStoreProviderOptions: SelectOption[] = [
  { label: 'Upstash', value: 'UPSTASH' },
];

// Text models a tenant's LLM config can be pointed at. Mirrors the TEXT entries of
// AI_MODEL_CATALOG (skillzen-sdk .../ai-model-setting-srv/services/enums/AIModel.enum.ts),
// which is what the AI Model Settings page reads. Add new models there first, then here.
const modelNameOptions: SelectOption[] = [
  { label: 'Gemini 3.7 Flash', value: 'gemini-3.7-flash' },
  { label: 'Gemini 3.6 Flash', value: 'gemini-3.6-flash' },
  { label: 'Gemini 3.5 Flash', value: 'gemini-3.5-flash' },
  { label: 'Gemini 2.5 Pro', value: 'gemini-2.5-pro' },
  { label: 'Gemini 2.5 Flash', value: 'gemini-2.5-flash' },
];

// Image models, mirroring the IMAGE entries of the same catalog — these are the models
// course/certification/learning-path image generation actually resolves against
// (see ImageGenerationProvider).
const imageModelOptions: SelectOption[] = [
  { label: 'Gemini 3.1 Flash Image', value: 'gemini-3.1-flash-image' },
  { label: 'Gemini 3 Pro Image', value: 'gemini-3-pro-image' },
  { label: 'Gemini 2.5 Flash Image', value: 'gemini-2.5-flash-image' },
];

/** Gemini 3.x model id — matches isGemini3() in the SDK's ModelDefaults. */
const isGemini3Model = (model?: string | null): boolean =>
  typeof model === 'string' && /^gemini-3(\.|-)/.test(model);

// Gemini 3.x is tuned for temperature 1.0 — Google warns that lower values cause looping
// and degraded output — while older models want a low, deterministic temperature.
const GEMINI_3_TEMPERATURE = 1.0;
const LEGACY_TEMPERATURE = 0.2;

// Email Provider options
const emailProviderOptions: SelectOption[] = [
  { label: 'SMTP', value: 'SMTP' },
];

// Form state is structurally a CreateTenantDto; in edit mode required fields may be
// pre-filled but typed the same way for register-path safety.
type TenantFormValues = CreateTenantDto;

export default function TenantForm({ initialData, onSubmit, isEdit = false, isLoading = false, tenantKey }: TenantFormProps) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TenantFormValues>({
    resolver: yupResolver(isEdit ? updateTenantSchema : createTenantSchema) as any,
    mode: 'onChange',
    defaultValues: initialData as TenantFormValues | undefined,
  });

  const { fields: domainFields, append: appendDomain, remove: removeDomain } = useFieldArray({
    control,
    name: 'domains',
  });

  // useFieldArray's generic doesn't model primitive string arrays cleanly; cast at the boundary.
  const {
    fields: allowedDomainFields,
    append: appendAllowedDomainRaw,
    remove: removeAllowedDomain,
    replace: replaceAllowedDomainsRaw,
  } = useFieldArray({
    control,
    name: 'allowed_domains' as never,
  });
  const appendAllowedDomain = (value: string) => appendAllowedDomainRaw(value as never);
  const replaceAllowedDomains = (values: string[]) => replaceAllowedDomainsRaw(values as never);

  // State for tenant key availability check
  const [keyAvailability, setKeyAvailability] = useState<{
    available: boolean | null;
    message: string;
    checking: boolean;
  }>({ available: null, message: '', checking: false });

  // State for tenant name availability check
  const [nameAvailability, setNameAvailability] = useState<{
    available: boolean | null;
    message: string;
    checking: boolean;
  }>({ available: null, message: '', checking: false });

  // Watch tenant_key to auto-fill bucket_name and domain URLs
  const watchedTenantKey = watch('tenant_key');
  const watchedTenantName = watch('name');

  // Debounce tenant key for availability check
  const debouncedTenantKey = useDebounce(watchedTenantKey, 500);
  const debouncedTenantName = useDebounce(watchedTenantName, 500);

  // Check tenant key availability
  useEffect(() => {
    const checkAvailability = async () => {
      if (!isEdit && debouncedTenantKey && debouncedTenantKey.length > 0) {
        setKeyAvailability({ available: null, message: '', checking: true });

        try {
          const result = await tenantService.checkTenantKeyAvailability(debouncedTenantKey);
          setKeyAvailability({
            available: result.available,
            message: result.message,
            checking: false
          });
        } catch (error) {
          setKeyAvailability({
            available: null,
            message: 'Error checking availability',
            checking: false
          });
        }
      } else if (isEdit || !debouncedTenantKey) {
        setKeyAvailability({ available: null, message: '', checking: false });
      }
    };

    checkAvailability();
  }, [debouncedTenantKey, isEdit]);

  // Check tenant name availability
  useEffect(() => {
    const checkNameAvailability = async () => {
      if (!isEdit && debouncedTenantName && debouncedTenantName.length > 0) {
        setNameAvailability({ available: null, message: '', checking: true });

        try {
          const result = await tenantService.checkTenantNameAvailability(debouncedTenantName);
          setNameAvailability({
            available: result.available,
            message: result.message,
            checking: false
          });
        } catch (error) {
          setNameAvailability({
            available: null,
            message: 'Error checking name availability',
            checking: false
          });
        }
      } else if (isEdit || !debouncedTenantName) {
        setNameAvailability({ available: null, message: '', checking: false });
      }
    };

    checkNameAvailability();
  }, [debouncedTenantName, isEdit]);

  // Auto-fill bucket_name, domain URLs, and first allowed_domain based on tenant_key
  useEffect(() => {
    if (!isEdit && watchedTenantKey) {
      // Remove skillzen_ prefix if present to get the clean domain name
      const cleanKey = watchedTenantKey.replace(/^skillzen_/, '');
      const bucketName = `skillzen_${watchedTenantKey}`.replace(/_/g, '-');
      const domainName = cleanKey.replace(/_/g, '-');

      // Set bucket name
      setValue('s3_config.bucket_name', bucketName);

      // Always auto-fill domain URLs based on current tenant_key
      if (domainName) {
        const uiUrl = `https://${domainName}.skillzen.ai`;
        const serviceUrl = `https://${domainName}-api.skillzen.ai`;

        setValue('domains.0.admin_url', uiUrl);
        setValue('domains.0.api_url', serviceUrl);

        // Auto-fill first allowed_domain with UI URL
        if (allowedDomainFields.length === 0) {
          appendAllowedDomain(uiUrl);
        } else {
          setValue('allowed_domains.0', uiUrl);
        }
      }
    }
  }, [watchedTenantKey, isEdit, setValue, allowedDomainFields.length, appendAllowedDomain]);

  // Watch provider selections
  const llmProvider = watch('ai_config.llm.provider');
  const vectorStoreProvider = watch('ai_config.vector_store.provider');
  const llmModelName = watch('ai_config.llm.config.model_name');

  // Temperature follows the model family: Gemini 3.x runs at 1.0 (anything lower makes it
  // loop or degrade, per Google's guidance), everything older at 0.2. Only fires when the
  // admin actually changes the model, so a hand-tuned value on an unopened form survives.
  const previousModelRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const model = llmModelName as string | undefined;
    const previous = previousModelRef.current;
    previousModelRef.current = model;

    // First run just records the loaded model — no overwrite.
    if (previous === undefined || previous === model || !model) return;

    setValue(
      'ai_config.llm.config.temperature' as any,
      isGemini3Model(model) ? GEMINI_3_TEMPERATURE : LEGACY_TEMPERATURE,
      { shouldDirty: true, shouldValidate: true }
    );
  }, [llmModelName, setValue]);

  // Reset form when switching modes or when initialData changes
  useEffect(() => {
    if (isEdit && initialData) {
      // Edit mode: populate with existing data
      const allowedDomainsArray = Array.isArray(initialData.allowed_domains) ? initialData.allowed_domains : [];

      console.log('[TenantForm] Edit mode - initialData.allowed_domains:', allowedDomainsArray);

      // image_generation_config.model is a multi-select, so the form always holds string[].
      // The DB stores either a single string or an array (and older tenants may carry a
      // comma-separated string from when this was a free-text field), so normalise all three.
      const normalizedInitial: TenantFormValues = { ...(initialData as TenantFormValues) };
      const img = normalizedInitial.ai_config?.image_generation_config;
      if (img && img.model !== undefined && img.model !== null) {
        const models = Array.isArray(img.model)
          ? img.model
          : String(img.model).split(',').map((m) => m.trim()).filter(Boolean);

        normalizedInitial.ai_config = {
          ...normalizedInitial.ai_config!,
          image_generation_config: { ...img, model: models as any },
        };
      }

      reset({
        ...normalizedInitial,
        allowed_domains: allowedDomainsArray,
      });

      // Sync field array AFTER reset
      setTimeout(() => {
        console.log('[TenantForm] Replacing allowed_domains field array with:', allowedDomainsArray);
        replaceAllowedDomains(allowedDomainsArray);
      }, 100);
    }
    // No fallback for create-mode-without-initialData: TenantFormPage guarantees
    // initialData is always set (via the management-info API) before rendering.
    //
    // Deps intentionally exclude `reset` and `replaceAllowedDomains`: the latter is a
    // fresh arrow on every render, and reset() re-renders the form, so including it
    // caused an infinite loop (reset → re-render → new fn ref → effect → reset …),
    // which froze the app (URL changed on navigation but the view never repainted).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isEdit]);

  const handleFormSubmit = async (formData: TenantFormValues) => {
    // Work on a typed mutable shallow copy so we can prune empty optional blocks
    const data: TenantFormValues & { [key: string]: unknown } = { ...formData };

    // Add skillzen_ prefix to tenant_key if not in edit mode and not already present
    if (!isEdit && data.tenant_key && !data.tenant_key.startsWith('skillzen_')) {
      data.tenant_key = `skillzen_${data.tenant_key}`;
    }

    // Clean up limits — convert empty strings to null; collapse to null/undefined if all empty
    if (data.limits) {
      const limits = data.limits as unknown as Record<string, number | null | undefined | ''>;
      (Object.keys(limits) as string[]).forEach((key) => {
        if (limits[key] === '' || limits[key] === undefined) {
          limits[key] = null;
        }
      });
      const hasAnyLimit = Object.values(limits).some((val) => val !== null && val !== undefined && val !== '');
      if (!hasAnyLimit) {
        // In edit mode, send null to clear; in create mode, send undefined
        (data as { limits: unknown }).limits = isEdit ? null : undefined;
      }
    }

    // Clean up allowed_domains - remove empty strings and send undefined if empty array
    if (data.allowed_domains) {
      const filtered = data.allowed_domains.filter((d) => !!d && d.trim() !== '');
      data.allowed_domains = filtered.length === 0 ? undefined : filtered;
    }

    // Normalize image_generation_config.model for the API: one model is sent as a string,
    // several as an array (the provider's fallback chain), nothing selected drops the block.
    // The multi-select gives us string[]; the string branch covers values loaded from older
    // tenants that were saved when this field was free text.
    if (data.ai_config?.image_generation_config) {
      const imgCfg = data.ai_config.image_generation_config;
      const raw: string | string[] | undefined = imgCfg.model;
      const list = Array.isArray(raw)
        ? raw.map((m) => String(m).trim()).filter(Boolean)
        : typeof raw === 'string'
          ? raw.split(',').map((m) => m.trim()).filter(Boolean)
          : [];

      if (list.length === 0) {
        delete data.ai_config.image_generation_config;
      } else {
        imgCfg.model = list.length === 1 ? list[0] : list;
      }
    }

    // Drop cloudflare_ai_gateway block if disabled and empty
    if (data.ai_config?.cloudflare_ai_gateway) {
      const g = data.ai_config.cloudflare_ai_gateway;
      if (!g.enabled && !g.token && !g.account_id && !g.gateway_id) {
        delete data.ai_config.cloudflare_ai_gateway;
      }
    }

    // Drop empty sub-providers and avatar_providers itself if all empty
    if (data.ai_config?.avatar_providers) {
      const ap = data.ai_config.avatar_providers;
      const didEmpty = !ap.d_id?.api_key;
      const heygenEmpty = !ap.heygen?.api_key && !ap.heygen?.base_url;
      if (didEmpty) delete ap.d_id;
      if (heygenEmpty) delete ap.heygen;
      if (didEmpty && heygenEmpty) {
        delete data.ai_config.avatar_providers;
      }
    }

    // Drop web_search if disabled and empty
    if (data.ai_config?.web_search) {
      const w = data.ai_config.web_search;
      if (!w.enabled && !w.provider && !w.tavily_api_key) {
        delete data.ai_config.web_search;
      }
    }

    // Drop onesignal_config if disabled and empty
    if (data.onesignal_config) {
      const o = data.onesignal_config;
      if (!o.enabled && !o.app_id && !o.rest_api_key) {
        delete (data as { onesignal_config?: unknown }).onesignal_config;
      }
    }

    await onSubmit(data as CreateTenantDto | UpdateTenantDto);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">

      {/* Basic Information */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Server className="w-5 h-5" />
          Basic Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tenant Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                {...register('name')}
              />
              {nameAvailability.checking && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                </div>
              )}
              {!nameAvailability.checking && nameAvailability.available === true && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </div>
              )}
              {!nameAvailability.checking && nameAvailability.available === false && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <XCircle className="w-4 h-4 text-red-500" />
                </div>
              )}
            </div>
            {errors.name?.message && (
              <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>
            )}
            {nameAvailability.message && !errors.name && (
              <p className={`text-xs mt-1 ${nameAvailability.available ? 'text-green-600' : 'text-red-600'}`}>
                {nameAvailability.message}
              </p>
            )}
          </div>

          {isEdit && tenantKey ? (
            <>
              <CopyableInputField
                label="Tenant Key"
                value={tenantKey}
                helperText="Cannot be changed after creation"
              />
              <div>
                <Select
                  name="status"
                  control={control}
                  label="Status"
                  options={statusOptions}
                  error={errors.status?.message}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tenant Key <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg font-mono text-sm">
                    skillzen_
                  </span>
                  <div className="flex-1 relative">
                    <InputField
                      fieldType="text"
                      required
                      error={errors.tenant_key?.message}
                      placeholder="your_tenant"
                      {...register('tenant_key')}
                    />
                    {keyAvailability.checking && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                      </div>
                    )}
                    {!keyAvailability.checking && keyAvailability.available === true && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      </div>
                    )}
                    {!keyAvailability.checking && keyAvailability.available === false && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <XCircle className="w-4 h-4 text-red-500" />
                      </div>
                    )}
                  </div>
                </div>
                {keyAvailability.message && !errors.tenant_key && (
                  <p className={`text-xs mt-1 ${keyAvailability.available ? 'text-green-600' : 'text-red-600'}`}>
                    {keyAvailability.message}
                  </p>
                )}
                {!keyAvailability.message && (
                  <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and underscores only</p>
                )}
              </div>

              <div>
                <Select
                  name="status"
                  control={control}
                  label="Status"
                  options={statusOptions}
                  error={errors.status?.message}
                  required
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Allowed Web Domains */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Allowed Web Domains
          </h2>
          <button
            type="button"
            onClick={() => appendAllowedDomain('')}
            className="btn btn-sm btn-secondary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Domain
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Specify allowed web domain URLs for CORS access control. Click "Add Domain" to restrict access.
        </p>

        {allowedDomainFields.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Globe className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No domain restrictions configured</p>
            <p className="text-sm">Click "Add Domain" above to add restrictions</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allowedDomainFields.map((field, index) => (
              <div key={field.id} className="flex items-center gap-3">
                <div className="flex-1">
                  {index === 0 ? (
                    // First domain is read-only (auto-filled from UI URL)
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Primary Domain (Auto-filled from UI URL)
                          <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Read-only</span>
                        </label>
                        <input
                          type="text"
                          {...register(`allowed_domains.${index}`)}
                          readOnly
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed focus:outline-none"
                        />
                      </div>
                      {!isEdit && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Service URL (Auto-filled from SERVICE URL)
                            <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Read-only</span>
                          </label>
                          <input
                            type="text"
                            value={watch('domains.0.api_url') || ''}
                            readOnly
                            disabled
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed focus:outline-none"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    // Other domains are editable
                    <InputField
                      label={`Additional Domain ${index}`}
                      fieldType="url"
                      placeholder="https://example.com"
                      error={errors.allowed_domains?.[index]?.message}
                      {...register(`allowed_domains.${index}`)}
                    />
                  )}
                </div>
                {index === 0 ? (
                  // First domain cannot be removed - show "Required" label
                  <div className="w-[88px] flex-shrink-0 flex items-center justify-center h-10 mt-7">
                    <span className="text-xs text-gray-500 font-medium">Required</span>
                  </div>
                ) : (
                  // Other domains can be removed
                  <button
                    type="button"
                    onClick={() => removeAllowedDomain(index)}
                    className="text-red-600 hover:text-red-700 p-2 mt-7"
                    title="Remove domain"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Domain Configurations */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Server className="w-5 h-5" />
            Domain Configurations
          </h2>
          <button
            type="button"
            onClick={() => appendDomain({ admin_url: '', api_url: '' })}
            className="btn btn-sm btn-secondary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Domain
          </button>
        </div>

        <div className="space-y-4">
          {domainFields.map((field, index) => (
            <div key={field.id} className={`border rounded-lg p-4 ${index === 0 ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">
                  {index === 0 ? (
                    <>
                      Primary Domain (Auto-filled)
                      <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded">Read-only</span>
                    </>
                  ) : (
                    `Additional Domain ${index}`
                  )}
                </h3>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => removeDomain(index)}
                    className="text-red-600 hover:text-red-700"
                    title="Remove domain"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {index === 0 && (
                  <span className="text-xs text-gray-500 font-medium">Required</span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {index === 0 ? (
                  <>
                    {/* First domain - Read-only */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UI URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register(`domains.${index}.admin_url`)}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SERVICE URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register(`domains.${index}.api_url`)}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed focus:outline-none"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Additional domains - Editable */}
                    <InputField
                      label="UI URL"
                      fieldType="url"
                      required
                      error={errors.domains?.[index]?.admin_url?.message}
                      {...register(`domains.${index}.admin_url`)}
                    />
                    <InputField
                      label="SERVICE URL"
                      fieldType="url"
                      required
                      error={errors.domains?.[index]?.api_url?.message}
                      {...register(`domains.${index}.api_url`)}
                    />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* S3 Configuration */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5" />
          S3 Configuration
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Access Key ID"
            fieldType="text"
            required
            error={errors.s3_config?.accessKeyId?.message}
            className="font-mono"
            {...register('s3_config.accessKeyId')}
          />

          <PasswordInputField
            label="Secret Access Key"
            required
            error={errors.s3_config?.secretAccessKey?.message}
            className="font-mono"
            {...register('s3_config.secretAccessKey')}
          />

          <InputField
            label="Endpoint"
            fieldType="url"
            required
            error={errors.s3_config?.endpoint?.message}
            {...register('s3_config.endpoint')}
          />

          <div>
            <Select
              name="s3_config.region"
              control={control}
              label="Region"
              options={awsRegionOptions}
              error={errors.s3_config?.region?.message}
            />
          </div>

          <InputField
            label="Bucket Name"
            fieldType="text"
            error={errors.s3_config?.bucket_name?.message}
            description={!isEdit ? 'Auto-filled from tenant key' : 'Cannot be changed after creation'}
            readOnly
            disabled
            className="bg-gray-100 text-gray-600 cursor-not-allowed"
            {...register('s3_config.bucket_name')}
          />
        </div>
      </div>

      {/* Email Configuration */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Email Configuration
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Select
              name="email_config.provider"
              control={control}
              label="Provider"
              options={emailProviderOptions}
              error={errors.email_config?.provider?.message}
              required
            />
          </div>

          <InputField
            label="Host Name"
            fieldType="text"
            required
            error={errors.email_config?.host_name?.message}
            {...register('email_config.host_name')}
          />

          <InputField
            label="Host Port"
            fieldType="number"
            required
            error={errors.email_config?.host_port?.message}
            {...register('email_config.host_port')}
          />

          <InputField
            label="Client ID"
            fieldType="text"
            required
            error={errors.email_config?.client_id?.message}
            {...register('email_config.client_id')}
          />

          <PasswordInputField
            label="Client Secret"
            required
            error={errors.email_config?.client_secret?.message}
            {...register('email_config.client_secret')}
          />

          <InputField
            label="From Address"
            fieldType="email"
            required
            error={errors.email_config?.from?.message}
            {...register('email_config.from')}
          />
        </div>
      </div>

      {/* AI Configuration */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Brain className="w-5 h-5" />
          AI Configuration
        </h2>

        {/* LLM Config */}
        <div className="mb-6">
          <h3 className="font-medium text-gray-900 mb-3">LLM Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Select
                name="ai_config.llm.provider"
                control={control}
                label="Provider"
                options={llmProviderOptions}
                error={errors.ai_config?.llm?.provider?.message}
                required
              />
            </div>

            {llmProvider && (
              <>
                <div>
                  <Select
                    name="ai_config.llm.config.model_name"
                    control={control}
                    label="Model Name"
                    options={modelNameOptions}
                    error={errors.ai_config?.llm?.config?.model_name?.message}
                    required
                  />
                </div>

                <InputField
                  label="Temperature"
                  fieldType="text"
                  required
                  error={errors.ai_config?.llm?.config?.temperature?.message}
                  description="Value between 0 and 1"
                  {...register('ai_config.llm.config.temperature')}
                />

                <div className="md:col-span-2">
                  <PasswordInputField
                    label="API Key"
                    required
                    error={errors.ai_config?.llm?.config?.api_key?.message}
                    className="font-mono"
                    {...register('ai_config.llm.config.api_key')}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Vector Store Config */}
        <div className="mb-6">
          <h3 className="font-medium text-gray-900 mb-3">Vector Store Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Select
                name="ai_config.vector_store.provider"
                control={control}
                label="Provider"
                options={vectorStoreProviderOptions}
                error={errors.ai_config?.vector_store?.provider?.message}
                required
              />
            </div>

            {vectorStoreProvider && (
              <>
                <InputField
                  label="URL"
                  fieldType="url"
                  required
                  error={errors.ai_config?.vector_store?.config?.url?.message}
                  {...register('ai_config.vector_store.config.url')}
                />

                <div className="md:col-span-2">
                  <PasswordInputField
                    label="Token"
                    required
                    error={errors.ai_config?.vector_store?.config?.token?.message}
                    className="font-mono"
                    {...register('ai_config.vector_store.config.token')}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Google Cloud Credentials */}
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Google Cloud Credentials</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Type"
              fieldType="text"
              required
              error={errors.ai_config?.google_cloud_credentials?.type?.message}
              {...register('ai_config.google_cloud_credentials.type')}
            />

            <InputField
              label="Project ID"
              fieldType="text"
              required
              error={errors.ai_config?.google_cloud_credentials?.project_id?.message}
              {...register('ai_config.google_cloud_credentials.project_id')}
            />

            <InputField
              label="Private Key ID"
              fieldType="text"
              required
              error={errors.ai_config?.google_cloud_credentials?.private_key_id?.message}
              {...register('ai_config.google_cloud_credentials.private_key_id')}
            />

            <InputField
              label="Client Email"
              fieldType="email"
              required
              error={errors.ai_config?.google_cloud_credentials?.client_email?.message}
              {...register('ai_config.google_cloud_credentials.client_email')}
            />

            <InputField
              label="Client ID"
              fieldType="text"
              required
              error={errors.ai_config?.google_cloud_credentials?.client_id?.message}
              {...register('ai_config.google_cloud_credentials.client_id')}
            />

            <InputField
              label="Universe Domain"
              fieldType="text"
              required
              error={errors.ai_config?.google_cloud_credentials?.universe_domain?.message}
              {...register('ai_config.google_cloud_credentials.universe_domain')}
            />

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Private Key <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={4}
                className={`input-field font-mono resize-none ${errors.ai_config?.google_cloud_credentials?.private_key ? 'border-red-500 focus:ring-red-500' : ''}`}
                {...register('ai_config.google_cloud_credentials.private_key')}
              />
              {errors.ai_config?.google_cloud_credentials?.private_key && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.ai_config.google_cloud_credentials.private_key.message}
                </p>
              )}
            </div>

            <InputField
              label="Auth URI"
              fieldType="url"
              required
              error={errors.ai_config?.google_cloud_credentials?.auth_uri?.message}
              {...register('ai_config.google_cloud_credentials.auth_uri')}
            />

            <InputField
              label="Token URI"
              fieldType="url"
              required
              error={errors.ai_config?.google_cloud_credentials?.token_uri?.message}
              {...register('ai_config.google_cloud_credentials.token_uri')}
            />

            <InputField
              label="Auth Provider Cert URL"
              fieldType="url"
              required
              error={errors.ai_config?.google_cloud_credentials?.auth_provider_x509_cert_url?.message}
              {...register('ai_config.google_cloud_credentials.auth_provider_x509_cert_url')}
            />

            <InputField
              label="Client Cert URL"
              fieldType="url"
              required
              error={errors.ai_config?.google_cloud_credentials?.client_x509_cert_url?.message}
              {...register('ai_config.google_cloud_credentials.client_x509_cert_url')}
            />
          </div>
        </div>
      </div>

      {/* CloudFlare AI Gateway */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Cloud className="w-5 h-5" />
          CloudFlare AI Gateway 
        </h2>

        <div className="mb-4">
          <Toggle
            name="ai_config.cloudflare_ai_gateway.enabled"
            control={control}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Account ID"
            fieldType="text"
            error={errors.ai_config?.cloudflare_ai_gateway?.account_id?.message}
            className="font-mono"
            {...register('ai_config.cloudflare_ai_gateway.account_id')}
          />

          <InputField
            label="Gateway ID"
            fieldType="text"
            error={errors.ai_config?.cloudflare_ai_gateway?.gateway_id?.message}
            {...register('ai_config.cloudflare_ai_gateway.gateway_id')}
          />

          <div className="md:col-span-2">
            <PasswordInputField
              label="Token"
              error={errors.ai_config?.cloudflare_ai_gateway?.token?.message}
              className="font-mono"
              {...register('ai_config.cloudflare_ai_gateway.token')}
            />
          </div>

          <InputField
            label="BYOK Alias"
            fieldType="text"
            description="Optional CF AIG BYOK alias"
            error={errors.ai_config?.cloudflare_ai_gateway?.cf_aig_byok_alias?.message}
            {...register('ai_config.cloudflare_ai_gateway.cf_aig_byok_alias')}
          />
        </div>
      </div>

      {/* Image Generation Config */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          Image Generation 
        </h2>

        <Select
          name="ai_config.image_generation_config.model"
          control={control}
          label="Model(s)"
          // Multiple models act as a fallback chain: the provider tries them in order, so a
          // model that refuses a prompt does not fail the whole course.
          isMulti
          options={imageModelOptions}
          helperText="Pick one or more. Extras act as fallbacks, tried in order."
          error={errors.ai_config?.image_generation_config?.model?.message}
        />
      </div>

      {/* Avatar Providers (D-ID + HeyGen) */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Avatar Providers 
        </h2>

        {/* D-ID */}
        <div className="mb-6">
          <h3 className="font-medium text-gray-900 mb-3">D-ID</h3>
          <PasswordInputField
            label="API Key"
            error={errors.ai_config?.avatar_providers?.d_id?.api_key?.message}
            className="font-mono"
            {...register('ai_config.avatar_providers.d_id.api_key')}
          />
        </div>

        {/* HeyGen */}
        <div>
          <h3 className="font-medium text-gray-900 mb-3">HeyGen</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-1">
              <PasswordInputField
                label="API Key"
                error={errors.ai_config?.avatar_providers?.heygen?.api_key?.message}
                className="font-mono"
                {...register('ai_config.avatar_providers.heygen.api_key')}
              />
            </div>
            <InputField
              label="Base URL"
              fieldType="text"
              description="e.g. https://api.heygen.com"
              error={errors.ai_config?.avatar_providers?.heygen?.base_url?.message}
              {...register('ai_config.avatar_providers.heygen.base_url')}
            />
          </div>
        </div>
      </div>

      {/* Web Search */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Web Search 
        </h2>

        <div className="mb-4">
          <Toggle
            name="ai_config.web_search.enabled"
            control={control}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Provider"
            fieldType="text"
            description="e.g. TAVILY"
            error={errors.ai_config?.web_search?.provider?.message}
            {...register('ai_config.web_search.provider')}
          />

          <PasswordInputField
            label="Tavily API Key"
            error={errors.ai_config?.web_search?.tavily_api_key?.message}
            className="font-mono"
            {...register('ai_config.web_search.tavily_api_key')}
          />
        </div>
      </div>

      {/* OneSignal Configuration */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5" />
          OneSignal (Push Notifications, Optional)
        </h2>

        <div className="mb-4">
          <Toggle
            name="onesignal_config.enabled"
            control={control}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="App ID"
            fieldType="text"
            error={errors.onesignal_config?.app_id?.message}
            className="font-mono"
            {...register('onesignal_config.app_id')}
          />

          <InputField
            label="Safari Web ID"
            fieldType="text"
            error={errors.onesignal_config?.safari_web_id?.message}
            className="font-mono"
            {...register('onesignal_config.safari_web_id')}
          />

          <div className="md:col-span-2">
            <PasswordInputField
              label="REST API Key"
              error={errors.onesignal_config?.rest_api_key?.message}
              className="font-mono"
              {...register('onesignal_config.rest_api_key')}
            />
          </div>
        </div>
      </div>

      {/* Tenant Limits */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Tenant Limits (Optional)
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Max Users"
            fieldType="number"
            error={errors.limits?.max_users?.message}
            {...register('limits.max_users')}
          />

          <InputField
            label="Max Course Generations"
            fieldType="number"
            error={errors.limits?.max_course_generations?.message}
            {...register('limits.max_course_generations')}
          />

          <InputField
            label="Max Document Stores"
            fieldType="number"
            error={errors.limits?.max_document_stores?.message}
            {...register('limits.max_document_stores')}
          />

          <InputField
            label="Max Files per Document Store"
            fieldType="number"
            error={errors.limits?.max_files_per_document_store?.message}
            {...register('limits.max_files_per_document_store')}
          />
        </div>
      </div>

      {/* Form Actions — full-width on mobile, right-aligned on sm+ */}
      <div className="flex items-center justify-stretch sm:justify-end">
        <button
          type="submit"
          disabled={
            isLoading ||
            (!isEdit && keyAvailability.available === false) ||
            (!isEdit && nameAvailability.available === false)
          }
          className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <Save className="w-4 h-4" />
          {isLoading ? 'Saving...' : isEdit ? 'Update Tenant' : 'Create Tenant'}
        </button>
      </div>
    </form>
  );
}
